import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, LogOut, ShieldCheck } from "lucide-react";
import logoLight from "@/assets/brand/lux-logo.png";
import logoLit from "@/assets/brand/lux-logo-lit.png";
import hornsSharp from "@/assets/brand/lux-horns.png";
import hornsGlow from "@/assets/brand/lux-horns-glow.png";
import { quickExit } from "@/lib/visitor";

const SPLASH_KEY = "lux_splash_seen"; // once per browser session
const AGE_KEY = "lux_age_confirmed"; // remembered on this device
// Legal pages stay reachable without confirming age (they are linked from the gate itself)
const GATE_EXEMPT = ["/terms", "/privacy", "/cookies", "/online-safety", "/complaints"];

function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
}

export function isAgeConfirmed() {
  try {
    return !!localStorage.getItem(AGE_KEY);
  } catch (e) {
    return true; // storage blocked: don't trap the visitor in a loop
  }
}

function initialStage(pathname) {
  if (GATE_EXEMPT.includes(pathname)) return null;
  if (!isAgeConfirmed()) return "gate";
  try {
    if (sessionStorage.getItem(SPLASH_KEY)) return null;
  } catch (e) {
    return null;
  }
  return reducedMotion() ? null : "splash";
}

function AgeGate({ onEnter }) {
  const btn = useRef(null);
  useEffect(() => {
    btn.current?.focus();
  }, []);
  return (
    <div
      className="lux-splash lux-noise fixed inset-0 z-[300] flex flex-col items-center justify-center bg-lux-bg px-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      aria-describedby="age-gate-text"
      data-testid="age-gate"
    >
      <div className="lux-splash-logo w-full max-w-md text-center">
        <img src={logoLit} alt="LUX" className="mx-auto h-16 w-auto select-none sm:h-20" draggable="false" />
        <p className="lux-label mt-10">Adults only</p>
        <h1 id="age-gate-title" className="mt-3 font-serif text-4xl leading-tight sm:text-5xl">Are you 18 or over?</h1>
        <p id="age-gate-text" className="mt-4 text-[15px] leading-relaxed text-lux-muted">
          LUX is a directory of verified, independent adult advertisers in London, Birmingham and Manchester. It contains no explicit
          imagery, but it is intended for adults only. By entering you confirm that you are at least 18 years old and accept our
          {" "}
          <a href="/terms" target="_blank" rel="noopener" className="lux-link" data-testid="age-gate-terms-link">Terms</a> and{" "}
          <a href="/privacy" target="_blank" rel="noopener" className="lux-link" data-testid="age-gate-privacy-link">Privacy policy</a>.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <button ref={btn} onClick={onEnter} className="lux-btn lux-btn-primary w-full text-[15px]" data-testid="age-gate-enter-button">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> I am 18 or over - enter
          </button>
          <button onClick={quickExit} className="lux-btn lux-btn-secondary w-full text-[15px]" data-testid="age-gate-leave-button">
            <LogOut className="h-4 w-4" aria-hidden="true" /> I am under 18 - leave
          </button>
        </div>
        <p className="mt-6 text-xs leading-relaxed text-lux-muted" data-testid="age-gate-storage-note">
          <Cookie className="mr-1 inline h-3.5 w-3.5 align-[-2px] text-lux-silver" aria-hidden="true" />
          Your answer is remembered on this device only, using your browser's local storage (no cookies, no account, no tracking). The same
          storage keeps your photo-blur setting and saved shortlist.{" "}
          <a href="/cookies" target="_blank" rel="noopener" className="lux-link" data-testid="age-gate-cookies-link">How LUX uses storage</a>
        </p>
      </div>
    </div>
  );
}

function Splash({ onDone }) {
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLeaving(true), 1700);
    const skip = () => setLeaving(true);
    window.addEventListener("keydown", skip);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", skip);
    };
  }, []);
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(onDone, 520);
    return () => clearTimeout(t);
  }, [leaving, onDone]);
  return (
    <div
      className={`lux-splash fixed inset-0 z-[300] flex items-center justify-center bg-lux-bg ${leaving ? "lux-splash-leaving" : ""}`}
      onClick={() => setLeaving(true)}
      role="presentation"
      aria-hidden="true"
      data-testid="splash-screen"
    >
      <div className="lux-noise absolute inset-0" />
      <div className="lux-splash-logo relative">
        <img src={logoLight} alt="" className="h-20 w-auto select-none sm:h-28" draggable="false" />
        <img src={hornsGlow} alt="" className="lux-splash-glow pointer-events-none absolute inset-0 h-full w-full select-none" draggable="false" />
        <img src={hornsSharp} alt="" className="lux-splash-horns pointer-events-none absolute inset-0 h-full w-full select-none" draggable="false" />
      </div>
      <p className="lux-splash-tag absolute bottom-14 left-0 right-0 text-center font-serif text-lg italic text-lux-silver sm:text-xl">Discretion, verified.</p>
      <span className="sr-only">Loading LUX</span>
    </div>
  );
}

/**
 * Public-site entry flow:
 *  1. Age gate (18+) on first visit - the answer is remembered on this device (localStorage).
 *  2. Entry animation once per session: the logo appears, the horns light up red, the screen fades away.
 */
export default function EntryScreen() {
  const { pathname } = useLocation();
  const [stage, setStage] = useState(() => initialStage(pathname));

  // Leaving an exempt legal page for the rest of the site still requires the age confirmation
  useEffect(() => {
    if (!stage && !GATE_EXEMPT.includes(pathname) && !isAgeConfirmed()) setStage("gate");
  }, [pathname, stage]);

  useEffect(() => {
    if (!stage) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [stage]);

  const enter = () => {
    try {
      localStorage.setItem(AGE_KEY, new Date().toISOString());
    } catch (e) {
      /* ignore */
    }
    setStage(reducedMotion() ? null : "splash");
  };
  const finishSplash = () => {
    try {
      sessionStorage.setItem(SPLASH_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setStage(null);
  };

  if (stage === "gate") return <AgeGate onEnter={enter} />;
  if (stage === "splash") return <Splash onDone={finishSplash} />;
  return null;
}
